import React, { useEffect, useState } from 'react';
import axios from "axios";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { Loading } from "@/layouts/buddy/loading";

interface ICompanySummaryItem {
    number: string;
    label: string;
    onClick?: () => void;
}

interface ICompanySummaryItems extends Array<ICompanySummaryItem> {}

interface ICompanySummary {
    active_candidates: number;
    active_jobs: number;
}

interface CompanySummaryProps {
    setActivePage: (page: string) => void;
    onSummaryFetched?: (activeJobs: number) => void;
}

const CompanySummary: React.FC<CompanySummaryProps> = ({ setActivePage, onSummaryFetched }) => {
    const { p_id } = useParams();

    const [items, setItems] = useState<ICompanySummaryItems>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    useEffect(() => {
        if (!p_id) {
            enqueueSnackbar(`No company ID provided.`, {
                variant: 'solid',
                state: 'danger'
            });
            setLoading(false);
            return;
        }

        const fetchCompanySummary = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get<ICompanySummary>(`${API_URL}/company/${p_id}/summary`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                const { active_candidates, active_jobs } = response.data;

                if (onSummaryFetched) {
                    onSummaryFetched(active_jobs);
                }

                const companySummaryItems: ICompanySummaryItems = [
                    {
                        number: active_jobs.toString(),
                        label: 'Active Jobs',
                        onClick: () => setActivePage('jobs'),
                    },
                    {
                        number: active_candidates.toString(),
                        label: 'Active Candidates',
                        onClick: () => setActivePage('candidates'),
                    }
                ];

                setItems(companySummaryItems);
            } catch (err: any) {
                console.error('Error fetching company summary:', err);
                if (err.response && err.response.status === 404) {
                    enqueueSnackbar(`Company not found or access denied.`, {
                        variant: 'solid',
                        state: 'danger'
                    });
                } else if (err.response && err.response.status === 401) {
                    enqueueSnackbar(`Unauthorized. Please log in.`, {
                        variant: 'solid',
                        state: 'danger'
                    });
                } else {
                    enqueueSnackbar(`Failed to fetch company summary.`, {
                        variant: 'solid',
                        state: 'danger'
                    });
                }
            } finally {
                setLoading(false);
            }
        };

        fetchCompanySummary();
    }, [API_URL, p_id, setActivePage, onSummaryFetched]);

    const renderItems = (item: ICompanySummaryItem, index: number) => (
        <React.Fragment key={index}>
            <button
                onClick={item.onClick}
                className={`flex flex-col items-center flex-1 gap-1 text-center focus:outline-none 
                           ${item.onClick ? 'cursor-pointer hover:text-primary' : ''}`}
            >
                <span className="text-gray-900 text-2xl lg:text-3xl leading-none font-semibold hover:text-primary">
                    {item.number}
                </span>
                <span className="text-gray-700 text-sm hover:text-primary">{item.label}</span>
            </button>
            {index !== items.length - 1 && (
                <span className="border-r border-r-gray-300 my-1 h-full"></span>
            )}
        </React.Fragment>
    );

    if (loading) return <Loading loading={loading} />;

    return (
        <div className="card">
            <div className="card-body">
                <div className="flex lg:px-10 py-1.5 gap-2">
                    {items.map((item, index) => renderItems(item, index))}
                </div>
            </div>
        </div>
    );
};

export { CompanySummary, type ICompanySummaryItem, type ICompanySummaryItems };
